import { useAuth0 } from '@auth0/auth0-react';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetFirebaseTokenV1UserFirebaseTokenGetQuery, useGetUserIdV1UserGetQuery } from 'store/api/api';
import { GetUserIdV1UserGetApiResponse } from 'store/api/apiGenerated';
import { SaviWebAuth0User } from './auth0.helpers';
import { selectAccessToken } from './userSlice';

export type LoggedInUser = SaviWebAuth0User &
  GetUserIdV1UserGetApiResponse & {
    isAuthenticated: true;
  };

export type NotLoggedInUser = {
  isAuthenticated: false;
};

export type UseUserState = (LoggedInUser | NotLoggedInUser) & { isLoading: boolean };

export const useUser = (): UseUserState => {
  const [loading, setLoading] = useState(true);
  const accessToken = useSelector(selectAccessToken);
  const { isAuthenticated, user: auth0User } = useAuth0();
  const { data: user, isLoading: getUserIsLoading } = useGetUserIdV1UserGetQuery(!accessToken ? skipToken : undefined);
  const { data: firebaseToken, isLoading: firebaseTokenIsLoading } = useGetFirebaseTokenV1UserFirebaseTokenGetQuery(
    !accessToken ? skipToken : undefined,
  );

  useEffect(() => {
    if (getUserIsLoading || firebaseTokenIsLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [getUserIsLoading, firebaseTokenIsLoading]);

  return useMemo(() => {
    if (isAuthenticated && auth0User && user && firebaseToken) {
      const auth0UserTyped = auth0User as SaviWebAuth0User;

      return { isAuthenticated: true, ...user, ...auth0UserTyped, isLoading: loading };
    }

    return { isAuthenticated: false, isLoading: loading };
  }, [isAuthenticated, auth0User, user, firebaseToken, loading]);
};
